import React, { useState, useEffect } from 'react';
import { VALID_CATEGORIES } from '../../timeline/constants';
import '../styles/MarkerEditor.css';

/**
 * MarkerEditor - Modal form for creating/editing map markers.
 */
function MarkerEditor({ marker, latlng, onSave, onCancel, timelineEvents = [] }) {
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState('');
  const [notes, setNotes] = useState('');
  const [color, setColor] = useState('');
  const [linkedEvents, setLinkedEvents] = useState([]);
  const [eventSearch, setEventSearch] = useState('');
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    if (marker) {
      setTitle(marker.title || '');
      setSummary(marker.summary || '');
      setLat(String(marker.lat != null ? marker.lat : ''));
      setLng(String(marker.lng != null ? marker.lng : ''));
      setCategory(marker.category || '');
      setTags(marker.tags ? marker.tags.join(', ') : '');
      setNotes(marker.notes || '');
      setColor(marker.color || '');
      setLinkedEvents(marker.linkedEvents || []);
    } else if (latlng) {
      setTitle('');
      setSummary('');
      setLat(String(latlng.lat.toFixed(6)));
      setLng(String(latlng.lng.toFixed(6)));
      setCategory('');
      setTags('');
      setNotes('');
      setColor('');
      setLinkedEvents([]);
    }
  }, [marker, latlng]);

  function handleSubmit(e) {
    e.preventDefault();
    const errs = [];
    if (!title.trim()) errs.push('Title is required');
    if (!lat || isNaN(Number(lat))) errs.push('Valid latitude is required');
    if (!lng || isNaN(Number(lng))) errs.push('Valid longitude is required');

    if (errs.length > 0) {
      setErrors(errs);
      return;
    }

    const data = {
      title: title.trim(),
      lat: Number(lat),
      lng: Number(lng),
    };
    if (summary.trim()) data.summary = summary.trim();
    if (category) data.category = category;
    if (tags.trim()) data.tags = tags.split(',').map(t => t.trim()).filter(Boolean);
    if (notes.trim()) data.notes = notes.trim();
    if (color.trim()) data.color = color.trim();
    if (linkedEvents.length > 0) data.linkedEvents = linkedEvents;

    onSave(data);
  }

  return (
    <div className="marker-editor-overlay" onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div className="marker-editor-modal">
        <div className="marker-editor-header">
          <h2>{marker ? 'Edit Marker' : 'Add Marker'}</h2>
          <button className="marker-editor-close" onClick={onCancel} aria-label="Close">×</button>
        </div>
        <form className="marker-editor-form" onSubmit={handleSubmit}>
          {errors.length > 0 && (
            <div className="marker-editor-errors">
              {errors.map((err, i) => <p key={i}>{err}</p>)}
            </div>
          )}

          <div className="marker-editor-field">
            <label htmlFor="marker-title">Title *</label>
            <input id="marker-title" type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Location name" />
          </div>

          <div className="marker-editor-row">
            <div className="marker-editor-field">
              <label htmlFor="marker-lat">Latitude *</label>
              <input id="marker-lat" type="number" step="any" value={lat} onChange={e => setLat(e.target.value)} placeholder="31.7683" />
            </div>
            <div className="marker-editor-field">
              <label htmlFor="marker-lng">Longitude *</label>
              <input id="marker-lng" type="number" step="any" value={lng} onChange={e => setLng(e.target.value)} placeholder="35.2137" />
            </div>
          </div>

          <div className="marker-editor-field">
            <label htmlFor="marker-summary">Summary</label>
            <input id="marker-summary" type="text" value={summary} onChange={e => setSummary(e.target.value)} placeholder="Brief description" />
          </div>

          <div className="marker-editor-field">
            <label htmlFor="marker-notes">Notes (Markdown)</label>
            <textarea id="marker-notes" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Detailed notes..." rows={5} />
          </div>

          <div className="marker-editor-row">
            <div className="marker-editor-field">
              <label htmlFor="marker-category">Category</label>
              <select id="marker-category" value={category} onChange={e => setCategory(e.target.value)}>
                <option value="">-- Select --</option>
                {VALID_CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div className="marker-editor-field">
              <label htmlFor="marker-color">Color</label>
              <div className="marker-editor-color-row">
                <input type="color" value={color || '#4A90D9'} onChange={e => setColor(e.target.value)} className="marker-editor-color-picker" />
                <input id="marker-color" type="text" value={color} onChange={e => setColor(e.target.value)} placeholder="#hex" />
              </div>
            </div>
          </div>

          <div className="marker-editor-field">
            <label htmlFor="marker-tags">Tags</label>
            <input id="marker-tags" type="text" value={tags} onChange={e => setTags(e.target.value)} placeholder="Comma-separated tags" />
          </div>

          <div className="marker-editor-field">
            <label>Linked Timeline Events</label>
            <div className="marker-editor-event-search">
              <input
                type="text"
                value={eventSearch}
                onChange={e => setEventSearch(e.target.value)}
                placeholder="Search timeline events..."
              />
              {eventSearch.trim() && (
                <div className="marker-editor-event-results">
                  {timelineEvents
                    .filter(ev =>
                      ev.title.toLowerCase().includes(eventSearch.toLowerCase()) &&
                      !linkedEvents.includes(ev.id)
                    )
                    .slice(0, 8)
                    .map(ev => (
                      <button
                        key={ev.id}
                        type="button"
                        className="marker-editor-event-option"
                        onClick={() => {
                          setLinkedEvents([...linkedEvents, ev.id]);
                          setEventSearch('');
                        }}
                      >
                        {ev.title}
                      </button>
                    ))
                  }
                  {timelineEvents.filter(ev =>
                    ev.title.toLowerCase().includes(eventSearch.toLowerCase()) &&
                    !linkedEvents.includes(ev.id)
                  ).length === 0 && (
                    <div className="marker-editor-event-none">No matching events</div>
                  )}
                </div>
              )}
            </div>
            {linkedEvents.length > 0 && (
              <div className="marker-editor-linked-chips">
                {linkedEvents.map(eventId => {
                  const ev = timelineEvents.find(e => e.id === eventId);
                  return (
                    <span key={eventId} className="marker-editor-linked-chip">
                      {ev ? ev.title : eventId}
                      <button
                        type="button"
                        onClick={() => setLinkedEvents(linkedEvents.filter(id => id !== eventId))}
                        aria-label={`Remove ${ev ? ev.title : eventId}`}
                      >
                        ×
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          <div className="marker-editor-actions">
            <button type="button" className="marker-editor-cancel-btn" onClick={onCancel}>Cancel</button>
            <button type="submit" className="marker-editor-save-btn">{marker ? 'Save Changes' : 'Add Marker'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default MarkerEditor;
